/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext } from 'react';
import { GetStaticProps, NextLayoutComponentType } from 'next';
import { IReciter } from '@/infrastructure/interfaces/i-reciter';
import { Api } from '@/api';
import { useTranslation } from 'next-i18next';
import MainLayout from '@/layouts/main';
import AppContainer from '@/components/layout/container';
import { localeList } from '@/public/locales/locale-list';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { Reciter } from '@/infrastructure/class/reciter';
import { useRouter } from 'next/router';
import ReciterDetailHeader from '@/components/reciters/detail/header';
import { allRecordsPaginationOptions } from '@/infrastructure/const/pagination';
import { IAudioSurah } from '@/infrastructure/interfaces/i-audio-surah';
import AudioSurahsList from '@/components/audio-surahs/list';
import { IBreadcrumb } from '@/components/common/breadcrumbs';
import { routes } from '@/infrastructure/const/routes';
import s from './styles.module.scss';
import {
  baseMarkupSchema,
  breadcrumbListMarkupSchema,
  organizationMarkupSchema,
  webPageMarkupSchema,
  webSiteMarkupSchema,
} from '@/infrastructure/markup-schemas';
import {
  BreadcrumbContext,
  IBreadcrumbContextType,
} from '@/infrastructure/context/breadcrumb-context';
import { getLocaleForUrl } from '@/infrastructure/functions/get-locale-for-url';
import SeoHead from '@/components/common/seo-head';
import { replaceWords } from '@/utils/helpers/replace-words';
import { addDomainForReciterImages } from '@/utils/helpers/add-domain-for-reciter-images';
import { decryptPageProps } from '@/utils/crypto/page-props-decrypt';
import { IEncryptedProps } from '@/infrastructure/interfaces/i-encrypted-props';

const api = Api.getInstance();
const RUQIA_GUID = process.env.RUQIA_GUID;
const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL;

interface IRuqiaDetailPageProps {
  reciter: IReciter;
  audioSurahs: IAudioSurah[];
  serverPage?: number;
}

const RuqiaDetailPage: NextLayoutComponentType<IEncryptedProps> = (props) => {
  const { data } = props;
  const decrypted = decryptPageProps<IRuqiaDetailPageProps>(data);
  const { reciter, audioSurahs, serverPage } = decrypted;

  const router = useRouter();
  const { locale } = router;

  const { setBreadcrumbs } = useContext(
    BreadcrumbContext
  ) as IBreadcrumbContextType;

  const { t: tCommon } = useTranslation('common');
  const { t: tLinks } = useTranslation('links');
  const { t: tMeta } = useTranslation('meta');

  const [reciterDetail, setReciterDetail] = useState<Reciter>(
    new Reciter(reciter, locale)
  );

  const breadcrumbs: IBreadcrumb[] = [
    {
      title: tLinks('home'),
      path: routes.home,
    },
    {
      title: reciter.title_en,
      path: routes.ruqia,
    },
  ];

  const canonicalURL = `${SITE_URL}${getLocaleForUrl(locale)}` + routes.ruqia;

  const htmlCanonical = (): string => {
    return `${canonicalURL}${
      Number(serverPage) > 1 ? `?page=${Number(serverPage)}` : ''
    }`;
  };
  const htmlTitle = (): string => {
    return `${replaceWords(tMeta('pageTitleBefore'), {
      title: String(reciterDetail.titleEn),
    })} ${
      Number(serverPage) > 1 ? `- ${tCommon('page')} ${Number(serverPage)}` : ''
    }`;
  };
  const htmlDescription = (): string => {
    return `${reciterDetail.biblioEn} ${
      Number(serverPage) > 1 ? `- ${tCommon('page')} ${Number(serverPage)}` : ''
    }`;
  };

  const shema = baseMarkupSchema([
    webPageMarkupSchema(routes.ruqia, htmlTitle(), htmlDescription()),
    webSiteMarkupSchema(),
    organizationMarkupSchema(),
    breadcrumbListMarkupSchema(breadcrumbs),
  ]);

  useEffect(() => {
    setReciterDetail(new Reciter(reciter, locale));
  }, [data, locale]);

  useEffect(() => {
    setBreadcrumbs(breadcrumbs);
  }, []);
  return (
    <>
      <SeoHead
        title={htmlTitle()}
        description={htmlDescription()}
        canonicalURL={htmlCanonical()}
        schema={shema}
      />
      <main>
        <ReciterDetailHeader
          className={s['detail-header']}
          data={reciterDetail}
          audioSurahs={audioSurahs}
          allowLike={false}
        />
        <AppContainer>
          <AudioSurahsList
            items={audioSurahs}
            partial={reciterDetail.isPartial}
            href={`${routes.reciters}/${reciterDetail.slug}`}
            serverPage={serverPage}
            reciterSlug={reciterDetail.slug}
          />
        </AppContainer>
      </main>
    </>
  );
};

RuqiaDetailPage.getLayout = (page) => <MainLayout>{page}</MainLayout>;

export const getStaticProps: GetStaticProps = async ({ locale = '' }) => {
  try {
    const { data } = await api.getReciterByGuid(String(RUQIA_GUID));
    const { reciter } = data;
    addDomainForReciterImages(reciter);

    const { data: audioSurahsResult } = await api.getAudioSurahsByReciterSlug(
      reciter.slug,
      allRecordsPaginationOptions
    );

    const { results: audioSurahs } = audioSurahsResult;

    const dataToEncrypt = {
      reciter,
      audioSurahs,
      serverPage: 1,
    };
    const { encryptPageProps } =
      await import('@/utils/crypto/page-props-encrypt');
    const encrypted = encryptPageProps(dataToEncrypt);

    return {
      props: {
        data: encrypted,
        ...(await serverSideTranslations(locale, localeList)),
      },
    };
  } catch (error) {
    console.error(error);
    return {
      notFound: true,
    };
  }
};

export default RuqiaDetailPage;
